// ─── Conversations — the panel as a real WhatsApp (owner, 2026-09-25, A2) ───
//
// « I want to work entirely from the system, never switching to my phone. »
// Text, voice notes, images, videos, files and quote-replies go out from here
// through the relay, exactly as typed; sending pauses the agent on that chat,
// same as the phone. Every message says who wrote it. See chat/ChatThread.tsx.
//
// 2026-09-26 (owner): a search box over EVERY order and message (the list only
// holds the newest 300 messages) and, beside the thread, the order card —
// status, client, address, price, 3D link, editor, and the next thing to do.
// See chat/OrderPanel.tsx and chat/orderCard.ts.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { useChatOpenSignal, useIsPhone, useVisualViewport } from './phone';
import { ChatThread, type ComposerPrefill } from './chat/ChatThread';
import { OrderPanel } from './chat/OrderPanel';
import { sendBoxSuggestion } from './chat/suggestion';
import { matchesConversation, searchable } from './chat/conversationFilter';
import { orderUrl } from './chat/orderCard';
import { settingsApi, whatsappApi, type ConversationSearchResult, type WhatsAppConversation } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { splitByPeriod } from './workbench';
import { ChatInboxBar } from './ChatInboxBar';
import { PaymentRequestButton } from '@/components/admin/PaymentRequestModal';
import { useFillHeight } from './fillHeight';
import { useViewingHeartbeat } from './chat/viewingHeartbeat';
// Pins + private names, per person (owner, 2026-10-21) — chat/ChatPrefsControls.tsx.
import { useChatPrefs, type ChatPrefsHandle } from './chat/useChatPrefs';
import { PinMark, PrefsError, PrefsRow, PrefsTitle } from './chat/ChatPrefsControls';
import { matchesCustomName, pinFirst, prefsFor, withPinnedMissing } from './chat/chatPrefs';
import type { DateRange } from '@/components/admin/DateRangePicker';
// The order on each row + ← → between chats (owner, 2026-10-30, agent-v2 DASHBOARD).
import { OrderChips, ChipPill } from './chat/OrderChips';
import { statusChip } from './chat/orderBadge';
import { isTypingTarget, keyStep, neighbourChat, swipeStep, type Step } from './chat/chatNav';

/** No period given = every conversation (the list before the timeline). */
const ALL_TIME: DateRange = {};

/**
 * The number to show, and the number wa.me must dial.
 *
 * ⚠️ A CHAT ID IS NOT ALWAYS A PHONE NUMBER. This was `chatId.split('@')[0]`,
 * which is right for `212600000000@c.us` and meaningless for a `@lid` privacy
 * id — so half the list read `142559141707851` and « Ouvrir WhatsApp » opened
 * a chat with a number that does not exist. The worker now resolves the real
 * one (see lib/conversationDisplay.ts); this only falls back to the id when a
 * thread genuinely has no number yet.
 */
const phoneOf = (chatId: string, phone?: string | null) =>
  phone || (chatId.endsWith('@c.us') ? chatId.slice(0, -'@c.us'.length) : chatId.split('@')[0] ?? chatId);

/** `orders.status` in the words the admin already uses elsewhere. An unknown
 *  status falls through to the raw value rather than being hidden — a stage we
 *  have not labelled yet is still more useful than no stage. */
const STAGE_LABEL: Record<string, string> = {
  'pending-album': 'attend ses photos',
  'album-submitted': 'album envoyé',
  'ready-to-print': 'prêt à imprimer',
  'printing': 'en impression',
  'ready-to-deliver': 'prêt à livrer',
  shipped: 'expédié',
  delivered: 'livré',
  paid: 'payé',
  cancelled: 'annulée',
};

const MATCH_LABEL: Record<ConversationSearchResult['match']['reason'], string> = {
  order: 'Commande',
  phone: 'Téléphone',
  name: 'Nom',
  message: 'Message',
};

function timeOf(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

/** The row's time, short: « 14:05 » today, « 28/10 » before. The full one is its tooltip. */
function shortTime(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Kept exported here for its guard test; lives with the thread rules now. */
export { sentAgo } from './chat/threadFormat';

const rowCls = (on: boolean) => `w-full text-left px-3 py-2.5 rounded-lg border transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
  on ? 'border-brand-400 bg-brand-50' : 'border-gray-200 bg-white hover:border-gray-300'
}`;

function ConversationList({
  conversations, selected, onSelect, prefs,
}: { conversations: WhatsAppConversation[]; selected: string | null; onSelect: (id: string) => void; prefs: ChatPrefsHandle }) {
  const { t } = useT();
  return (
    <div className="space-y-1.5">
      {conversations.map((c) => {
        // The person's own pin and private name (never anyone else's).
        const pref = prefs.ready ? prefs.prefOf(c.chat_id, c.phone) : null;
        const custom = pref?.custom_name ?? null;
        return (
        <PrefsRow key={c.chat_id} prefs={prefs} chatId={c.chat_id} phone={c.phone}>
        {/* The card: the row button (name, number, last message) and, under
            it, the order line — `#1249` · status² · Payé — which holds its own
            small button (her other orders), so it sits beside the row button,
            never inside it. A tap anywhere on the card opens the chat. */}
        <div data-conv-row={c.chat_id} onClick={() => onSelect(c.chat_id)}
          className={`${rowCls(selected === c.chat_id)} ${prefs.ready ? 'pr-12' : ''}`}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(c.chat_id); }}
          aria-current={selected === c.chat_id ? 'true' : undefined}
          className="block w-full text-left cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <div className="flex items-center gap-2">
            {pref?.pinned_at && <PinMark />}
            <span className="text-[12.5px] font-semibold text-gray-800 truncate">
              {custom || c.name || phoneOf(c.chat_id, c.phone)}
            </span>
            {/* Two different signals, never the same colour: a ready suggestion
                is neutral, "needs a human" is a warning. */}
            {c.needs_human ? (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {t('à voir')}
              </span>
            ) : c.has_draft ? (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {t('proposition')}
              </span>
            ) : null}
          </div>
          {/* ⚠️ THE NAME LINE ALONE WAS NOT ENOUGH. `c.name` comes from
              WhatsApp's push_name, which is null for most customers, so this
              list read as a column of raw phone numbers. The worker now falls
              back to the name on their order and sends the stage with it —
              which is the other thing you want to know before opening a
              conversation. Both are omitted rather than shown empty. */}
          {/* The number, when the row is headed by a NAME — knowing which
              Fadwa, and being able to read it back to a courier, is the whole
              reason he asked for it. */}
          {custom ? (
            // The private name heads the row; what WhatsApp knows stays visible, small.
            <p className="text-[10.5px] text-gray-500 tabular-nums mt-0.5 truncate">
              {c.name ? `${c.name} · ` : ''}{phoneOf(c.chat_id, c.phone)}
            </p>
          ) : c.name && c.phone && (
            <p className="text-[10.5px] text-gray-500 tabular-nums mt-0.5">{c.phone}</p>
          )}
          {/* An older worker sends no `orders`: the old one-line stage. */}
          {!c.orders && (c.order_name || c.stage) && (
            <p className="text-[10.5px] text-gray-500 truncate mt-0.5">
              {c.order_name}
              {c.order_name && c.stage ? ' · ' : ''}
              {c.stage ? t(STAGE_LABEL[c.stage] ?? c.stage) : ''}
            </p>
          )}
          <p className="flex items-baseline gap-2 mt-0.5">
            <span className="min-w-0 flex-1 text-[11px] text-gray-500 truncate">{c.last_body ?? (c.count === 0 && pref?.pinned_at ? t('Conversation épinglée — plus ancienne que la liste') : '—')}</span>
            <span className="shrink-0 text-[10px] text-gray-400 tabular-nums" title={timeOf(c.last_at)}>{shortTime(c.last_at)}</span>
          </p>
        </button>
        <OrderChips orders={c.orders} className="mt-1" />
        </div>
        </PrefsRow>
        );
      })}
    </div>
  );
}

// ─── Search ──────────────────────────────────────────────────────────────────

type ResultRow = Pick<ConversationSearchResult, 'key' | 'chat_id' | 'shopify_order_id' | 'name' | 'phone' | 'order_name' | 'stage' | 'last_body' | 'last_at'>
  & { match: ConversationSearchResult['match'] | null };

function SearchBox({ value, onChange, inputRef }: {
  value: string; onChange: (v: string) => void; inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const { t } = useT();
  return (
    <div className="relative">
      <label htmlFor="conversation-search" className="sr-only">{t('Rechercher une conversation')}</label>
      <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
      <input
        ref={inputRef}
        id="conversation-search" type="search" value={value} autoComplete="off" spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onChange(''); }}
        placeholder={t('Commande, nom, tél., message…')}
        className="w-full h-11 rounded-lg border border-gray-200 bg-white pl-9 pr-10 text-[16px] md:text-[13px] text-gray-900 placeholder:text-gray-400 transition-colors duration-150 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button type="button" onClick={() => { onChange(''); inputRef.current?.focus(); }}
          aria-label={t('Effacer la recherche')}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

function SearchResults({ rows, selected, onSelect, status, prefs }: {
  rows: ResultRow[]; selected: string | null; onSelect: (row: ResultRow) => void; status: React.ReactNode; prefs: ChatPrefsHandle;
}) {
  const { t } = useT();
  return (
    <div className="space-y-1.5">
      <p className="text-[11.5px] text-gray-500 px-1 tabular-nums" aria-live="polite">{status}</p>
      {rows.map((r) => {
        const pref = prefs.ready && r.chat_id ? prefs.prefOf(r.chat_id, r.phone) : null;
        const custom = pref?.custom_name ?? null;
        const original = r.name || (r.chat_id ? phoneOf(r.chat_id, r.phone) : r.phone) || r.order_name || '—';
        const head = (
          <>
            <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-800 min-w-0">
              {pref?.pinned_at && <PinMark />}
              <span className="truncate">{custom || original}</span>
            </span>
            {custom
              ? <span className="block text-[10.5px] text-gray-500 tabular-nums mt-0.5 truncate">{r.name && r.phone ? `${r.name} · ${r.phone}` : original}</span>
              : r.name && r.phone && <span className="block text-[10.5px] text-gray-500 tabular-nums mt-0.5">{r.phone}</span>}
            {(r.order_name || r.stage) && (
              <span className="block text-[10.5px] text-gray-500 truncate mt-0.5">
                {r.order_name}{r.order_name && r.stage ? ' · ' : ''}{r.stage ? t(STAGE_LABEL[r.stage] ?? r.stage) : ''}
              </span>
            )}
            {r.match && (
              <span className="block text-[11px] text-gray-700 mt-1 line-clamp-2">
                <span className="font-medium text-brand-700">{t(MATCH_LABEL[r.match.reason])}</span>
                {r.match.reason === 'message' && r.match.snippet ? ` — « ${r.match.snippet} »` : ''}
              </span>
            )}
            {!r.chat_id && (
              <span className="block text-[11px] text-amber-800 mt-1">{t('Pas de conversation WhatsApp — ouvrir la commande')}</span>
            )}
            {r.last_at && <span className="block text-[10px] text-gray-400 mt-0.5 tabular-nums">{timeOf(r.last_at)}</span>}
          </>
        );
        return r.chat_id ? (
          <button key={r.key} type="button" onClick={() => onSelect(r)} className={rowCls(selected === r.chat_id)}>
            {head}
          </button>
        ) : (
          <a key={r.key} href={orderUrl(r.shopify_order_id ?? '')} target="_blank" rel="noopener noreferrer" className={`block ${rowCls(false)}`}>
            {head}
          </a>
        );
      })}
    </div>
  );
}

// ─── The thread ──────────────────────────────────────────────────────────────

function Thread({ chatId, phone, name, prefill, fill = false }: {
  chatId: string; phone?: string | null; name?: string | null; prefill: ComposerPrefill;
  /** The phone's full-screen chat: the notes stay compact, the thread fills the rest. */
  fill?: boolean;
}) {
  const { t } = useT();
  const { data, isLoading } = useQuery({
    queryKey: ['whatsapp-conversation', chatId],
    queryFn: () => whatsappApi.conversation(chatId),
    refetchInterval: 15_000,
  });
  // « I am looking at this chat » (owner, 2026-10-18): the WhatsApp ping to
  // whoever holds it stays quiet meanwhile. Only where those pings are on.
  useViewingHeartbeat(chatId, Boolean(data?.takeover?.alerts));
  const reactions = Boolean(data?.takeover?.reactions);

  if (isLoading) return <p className={`text-[12.5px] text-gray-400 ${fill ? 'p-4' : ''}`}>{t('Chargement…')}</p>;

  const messages = data?.messages ?? [];
  const draft = data?.draft ?? null;
  const stale = data?.draft_stale ?? false;
  // Written by the worker — see describeDraftStatus there for why the
  // translation lives on one side only.
  const status = data?.draft_status ?? null;
  // ⚠️ THE RESOLVED NUMBER, NOT THE CHAT ID — a @lid here produced a wa.me
  // link to nobody.
  const waNumber = phoneOf(chatId, phone);

  return (
    <div className={fill ? 'flex flex-col h-full min-h-0' : 'space-y-3'}>
      {/* ─── WHY THE ASSISTANT DID (OR DID NOT) ANSWER ─────────────────────
          2026-08-29: « if there is an issue why it stops should appear so i
          can fix it ». The one line that explains the silence; its suggested
          reply is already in the box below, editable. */}
      {(status?.detail || draft?.error || (stale && draft?.reply)) && (
        <div className={fill ? 'shrink-0 max-h-[4.75rem] overflow-y-auto space-y-1 px-2 py-1 bg-white dark:bg-transparent text-[11.5px]' : 'space-y-1.5'}>
          {draft?.error && (
            <p className="text-[11.5px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" role="alert">
              {t('L’assistant n’a pas pu répondre :')} {draft.error}
            </p>
          )}
          {status?.detail && (
            <p className={`text-[11.5px] rounded-lg px-3 py-2 ${
              status.state === 'no_reply' || status.state === 'needs_human' || status.state === 'blocked'
                ? 'text-amber-900 bg-amber-50 border border-amber-200'
                : 'text-gray-600 bg-gray-50 border border-gray-100'
            }`}>
              {status.state === 'pending' && <span aria-hidden>⏳ </span>}
              <span className="font-semibold">{status.title}{status.detail ? ' — ' : ''}</span>{status.detail}
              {/* The code is for whoever fixes it — never the headline. */}
              {status.code && <span className="block mt-0.5 text-[10.5px] opacity-60">{t('Détails : {code}', { code: status.code })}</span>}
            </p>
          )}
          {stale && draft?.reply && (
            <p className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {t('Un nouveau message est arrivé depuis — la proposition de l’assistant n’en tient pas compte.')}
            </p>
          )}
        </div>
      )}

      {/* A2 (owner, 2026-09-25): the panel is a real WhatsApp — see chat/ChatThread. */}
      {fill ? (
        <div className="flex-1 min-h-0">
          <ChatThread chatId={chatId} messages={messages} suggestion={sendBoxSuggestion(draft, messages)} prefill={prefill} fill reactions={reactions} />
        </div>
      ) : (
        <ChatThread chatId={chatId} messages={messages} suggestion={sendBoxSuggestion(draft, messages)}
          title={name ? `${name} · ${waNumber}` : waNumber} prefill={prefill} reactions={reactions} />
      )}

      <p className={`text-[10.5px] text-gray-400 ${fill ? 'hidden' : ''}`}>
        <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
          {t('Ouvrir cette conversation dans WhatsApp')}
        </a>
      </p>
    </div>
  );
}

/**
 * « Repris par Omar · notifications actives » (owner, 2026-10-18; push since
 * 10-19) — whoever holds the chat gets a PUSH on their own devices when the
 * client writes (worker lib/takeoverPing.ts). Nobody else does: when the
 * holder has no device with notifications on, the line says nobody is told.
 * Pure, for its test.
 */
export function takeoverLine(
  hold: { owner_name: string | null; recipient: 'holder' | 'none' },
  t: (s: string, v?: Record<string, string | number>) => string,
): string {
  const who = hold.owner_name || t('un humain');
  if (hold.recipient === 'none') return t('Repris par {name} · pas de notification (aucun appareil activé)', { name: who });
  return t('Repris par {name} · notifications actives', { name: who });
}

/**
 * Take the conversation, or give it back.
 *
 * ⚠️ PAUSED IS NOT DEAF, and the wording says so, because that is the part
 * people get wrong. The assistant keeps receiving everything — photos still
 * attach, batches still group, facts still update — it simply stops REPLYING.
 * A pause that also stopped listening would resume knowing nothing about the
 * conversation a human just had.
 *
 * ⚠️ IT TELLS THE TRUTH ABOUT THE MASTER SWITCH (2026-09-26). The switch was
 * OFF for 19 hours while this banner read « Assistant actif sur cette
 * conversation » over every silent chat. Off for everyone is said first.
 */
function HandoverBar({ chatId, compact = false }: { chatId: string; compact?: boolean }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['whatsapp-conversations'],
    queryFn: () => whatsappApi.conversations(),
  });
  // Same cache as the master switch in Settings.
  const { data: settingsData } = useQuery({
    queryKey: ['app-settings', 'agent_test_numbers'],
    queryFn: () => settingsApi.get(),
    staleTime: 30_000,
  });
  const chat = (data?.conversations ?? []).find((c) => c.chat_id === chatId) as
    (WhatsAppConversation & { agent_paused_at?: string | null; agent_paused_by?: string | null; complaint_open_at?: string | null; complaint_quote?: string | null }) | undefined;
  // Who holds it, from the thread's own read (the same cache as the thread —
  // no second request). 2026-10-18: « Repris par [name] · alertes WhatsApp actives ».
  const { data: threadData } = useQuery({
    queryKey: ['whatsapp-conversation', chatId],
    queryFn: () => whatsappApi.conversation(chatId),
  });
  const hold = threadData?.takeover ?? null;
  const paused = Boolean(chat?.agent_paused_at) || Boolean(hold?.paused);
  const heldLine = paused && hold?.alerts ? takeoverLine(hold, t) : null;
  // The list only covers recent chats — the thread's own read covers the rest
  // (an August complaint opened from search had no bar, 2026-10-27).
  const complaintOpenAt = chat?.complaint_open_at ?? threadData?.complaint?.open_at ?? null;
  const complaintQuote = chat?.complaint_quote ?? threadData?.complaint?.quote ?? null;
  const s = (settingsData?.settings ?? null) as Record<string, unknown> | null;
  // Unknown until the settings arrive: say nothing rather than guess.
  const allOff = s !== null && s.assistant_enabled !== true;
  const draftOnly = s !== null && s.assistant_enabled === true && s.assistant_send_enabled !== true;

  const mut = useMutation({
    mutationFn: async (next: boolean): Promise<void> => {
      if (next) await whatsappApi.pauseChat(chatId);
      else await whatsappApi.resumeChat(chatId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] });
      return qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });
  // « Plainte résolue » (owner, 2026-10-15): while a complaint is open the agent
  // stays paused for this client — only a person closes it.
  const resolve = useMutation({
    mutationFn: () => whatsappApi.resolveComplaint(chatId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] });
      return qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });

  if (complaintOpenAt) {
    return (
      <div className={`${compact ? 'border-b px-3 py-1.5' : 'mb-3 rounded-xl border px-4 py-3'} flex items-center justify-between gap-3 border-red-200 bg-red-50`} role="status">
        <div className="min-w-0">
          <p className={`${compact ? 'text-[12px] leading-snug' : 'text-[12.5px]'} font-semibold text-red-800`}>
            {t('Plainte ouverte depuis le {d} — l’agent reste en pause pour ce client.', { d: new Date(complaintOpenAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) })}
          </p>
          {!compact && complaintQuote && <p className="text-[11.5px] text-red-900/80 mt-0.5 leading-snug truncate">« {complaintQuote} »</p>}
        </div>
        <Button size="sm" variant="primary" loading={resolve.isPending}
          onClick={() => { if (window.confirm(t('Marquer la plainte comme résolue ? L’agent reprend pour ce client (relances, offres, reprise après 15 min).'))) resolve.mutate(); }}>
          {t('Plainte résolue')}
        </Button>
      </div>
    );
  }

  if (allOff) {
    return (
      <div className={compact ? 'border-b border-red-200 bg-red-50 px-3 py-2' : 'mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3'} role="status">
        <p className="text-[12.5px] font-semibold text-red-800">{t('Assistant éteint pour tout le monde.')}</p>
        <p className={`text-[11.5px] text-red-900/80 mt-0.5 leading-snug ${compact ? 'hidden' : ''}`}>
          {t('Il ne répond à aucun client. Pour le rallumer : Cerveau → « Laisser l’assistant préparer des réponses ».')}
        </p>
      </div>
    );
  }

  return (
    <div className={`${compact ? 'border-b px-3 py-1.5' : 'mb-3 rounded-xl border px-4 py-3'} flex items-center justify-between gap-3 ${
      paused || draftOnly ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
    }`}>
      <div className="min-w-0">
        <p className={`${compact ? 'text-[12px] leading-snug' : 'text-[12.5px]'} font-semibold ${paused || draftOnly ? 'text-amber-900' : 'text-gray-800'}`}
          data-takeover-line={heldLine ? '' : undefined}>
          {heldLine ? heldLine
            : paused ? t('Assistant en pause — un humain gère cette conversation.')
            : draftOnly ? t('L’assistant prépare des réponses mais n’envoie rien tout seul.')
            : t('Assistant actif sur cette conversation.')}
        </p>
        <p className={`text-[11.5px] text-gray-600 mt-0.5 leading-snug ${compact ? 'hidden' : ''}`}>
          {paused
            ? t('Il continue de tout recevoir (photos, infos) mais ne répond plus. {who}', {
              who: chat?.agent_paused_by ? t('Mis en pause par {x}.', { x: chat.agent_paused_by }) : '',
            })
            : draftOnly ? t('Chaque réponse attend qu’un humain l’envoie. L’envoi automatique se règle dans Réglages.')
            : t('Prenez la main pour qu’il arrête de répondre. Il continuera d’écouter.')}
        </p>
      </div>
      <Button
        size="sm"
        variant={paused ? 'primary' : 'secondary'}
        loading={mut.isPending}
        onClick={() => mut.mutate(!paused)}
      >
        {paused ? t('Réactiver l’assistant') : t('Prendre la main')}
      </Button>
    </div>
  );
}

export function ConversationsTab({ chatId, initialChatId = null, onSelectChat, onCloseChat, range = ALL_TIME, onShowAll }: {
  /** The open chat, from the URL (`&chat=`). Given = the page owns it (app-feel plan, builder B). */
  chatId?: string | null;
  /** Without `chatId`: where to start, the component keeping its own choice. */
  initialChatId?: string | null;
  /** Open a chat — the page writes it in the URL (pushed on a phone, so Back returns to the list).
   *  `replace`: ← → / a swipe moves to the next chat in place — Back still returns to the list. */
  onSelectChat?: (chatId: string, opts?: { replace?: boolean }) => void;
  /** The phone chat's back arrow. */
  onCloseChat?: () => void;
  /** The page's timeline (owner 2026-09-26: 7 days by default). */
  range?: DateRange;
  onShowAll?: () => void;
} = {}) {
  const { t } = useT();
  const isPhone = useIsPhone();
  // Opened from the Inbox, the Pipeline, Live or the list: the chat is in the URL.
  const controlled = chatId !== undefined;
  const [own, setOwn] = useState<string | null>(initialChatId);
  useEffect(() => { if (initialChatId) setOwn(initialChatId); }, [initialChatId]);
  const selected = controlled ? (chatId ?? null) : own;
  const setSelected = (id: string, opts?: { replace?: boolean }) => (controlled && onSelectChat
    ? (opts ? onSelectChat(id, opts) : onSelectChat(id)) : setOwn(id));
  const closeChat = () => (controlled && onCloseChat ? onCloseChat() : setOwn(null));

  // A chat opened from the search may be older than the loaded list: keep the
  // name and number the search found, for the thread's header.
  const [picked, setPicked] = useState<ResultRow | null>(null);
  const openFromSearch = (r: ResultRow) => { if (r.chat_id) { setPicked(r); setSelected(r.chat_id); } };
  const openFromList = (id: string) => { setPicked(null); setSelected(id); };

  // Desktop: one frame down to the window bottom — every column scrolls on its own.
  const [frameRef, frameH] = useFillHeight(!isPhone);
  // ⤢ The chat alone, full width (owner, 2026-10-08: « make the chat bigger »).
  // Remembered per browser; the same button brings the columns back.
  const [wide, setWide] = useState<boolean>(() => { try { return localStorage.getItem('adm-chat-wide') === '1'; } catch { return false; } });
  const toggleWide = () => setWide((w) => { try { localStorage.setItem('adm-chat-wide', w ? '0' : '1'); } catch { /* private mode */ } return !w; });
  // Full-screen chat: Échap gives the page back (a dialog that handles Échap itself wins).
  useEffect(() => {
    if (!wide) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('[role="dialog"]')) return;
      setWide(false);
      try { localStorage.setItem('adm-chat-wide', '0'); } catch { /* private mode */ }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wide]);
  // After ✓ / ✗ / snooze: the next chat that needs a person (none left = the list).
  const openNext = (id: string | null) => (id ? openFromList(id) : closeChat());

  // ─── ← → between chats, WITHOUT handling them (owner, 2026-10-30) ─────────
  // « ← → arrows (swipe on phone, keyboard keys) to move to prev/next chat
  // without handling it. » In the order of the list on screen (the search
  // results while searching). Nothing is marked, sent or snoozed — the chat is
  // only opened. The ids are written at render time; the handlers read them.
  const navRef = useRef<string[]>([]);
  const selectedRef = useRef<string | null>(selected);
  selectedRef.current = selected;
  const stepChat = (step: Step) => {
    const id = neighbourChat(navRef.current, selectedRef.current, step);
    if (!id) return false;
    setPicked(null);
    setSelected(id, { replace: true });
    return true;
  };
  const stepRef = useRef(stepChat);
  stepRef.current = stepChat;
  useEffect(() => {
    if (isPhone) return;
    const onKey = (e: KeyboardEvent) => {
      const step = keyStep(e);
      if (!step || e.defaultPrevented) return;
      // Never while typing (the reply box, the search) or in a control that
      // uses arrows itself; never under an open dialog.
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)
        || document.querySelector('[role="dialog"]')) return;
      if (stepRef.current(step)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPhone]);
  // The row stays in view as ← → walks the list.
  useEffect(() => {
    if (!selected || isPhone || typeof document === 'undefined') return;
    const el = document.querySelector(`[data-conv-row="${selected.replace(/["\\]/g, '\\$&')}"]`) as HTMLElement | null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selected, isPhone]);

  // A recommended message from the order card, for the reply box.
  const [prefill, setPrefill] = useState<ComposerPrefill>(null);
  useEffect(() => { setPrefill(null); }, [selected]);

  // ─── Search: instant over the loaded list, then everything (debounced) ────
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(id);
  }, [query]);
  const searching = searchable(query);
  const server = useQuery({
    queryKey: ['whatsapp-search', debounced],
    queryFn: () => whatsappApi.searchConversations(debounced),
    enabled: searchable(debounced),
    staleTime: 15_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['whatsapp-conversations'],
    queryFn: () => whatsappApi.conversations(),
    refetchInterval: 15_000,
  });
  const everyone = data?.conversations ?? [];
  // The signed-in person's pins and private names — theirs alone.
  const prefs = useChatPrefs();
  // A pinned chat stays listed even once it is older than the newest 300 messages.
  const listed = prefs.ready ? withPinnedMissing(everyone, prefs.prefs) : everyone;
  // ⚠️ THE PERIOD: chats with a message in it. The chat you opened always stays,
  // whatever its date — the Inbox and the Pipeline open old ones on purpose.
  // So does a pinned one: a pin means « keep it at hand ».
  const period = splitByPeriod(listed, (c) => c.last_at, range,
    (c) => c.chat_id === selected || Boolean(prefs.ready && prefs.prefOf(c.chat_id, c.phone)?.pinned_at));
  // Pinned on top (newest pin first); the rest keep their order.
  const conversations = pinFirst(period.shown, prefs.ready ? prefs.prefs : []);

  if (isLoading) return <p className="text-[12.5px] text-gray-400">{t('Chargement…')}</p>;

  if (!everyone.length) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center">
        <p className="text-[13px] font-medium text-gray-700">{t('Aucune conversation pour le moment.')}</p>
        <p className="text-[11.5px] text-gray-500 mt-1 max-w-sm mx-auto leading-snug">
          {t('Les messages reçus sur le numéro connecté apparaîtront ici.')}
        </p>
      </div>
    );
  }

  // A search looks everywhere, whatever the period: the server's results first
  // (every order and message), then what the loaded list already matches.
  let rows: ResultRow[] = [];
  let status: React.ReactNode = null;
  if (searching) {
    const serverRows: ResultRow[] = server.data?.results ?? [];
    const seen = new Set(serverRows.map((r) => r.chat_id).filter(Boolean));
    const local: ResultRow[] = listed
      .filter((c) => !seen.has(c.chat_id) && (matchesConversation(c, query, (s) => t(STAGE_LABEL[s] ?? s))
        // The private name is searchable — by its owner only (the server never sees it).
        || (prefs.ready && matchesCustomName(prefs.prefOf(c.chat_id, c.phone), query))))
      .map((c) => ({
        key: c.chat_id, chat_id: c.chat_id, shopify_order_id: null, name: c.name, phone: c.phone,
        order_name: c.order_name ?? null, stage: c.stage ?? null, last_body: c.last_body, last_at: c.last_at, match: null,
      }));
    rows = [...serverRows, ...local];
    // A private name on a chat older than the loaded list: still found, by its owner.
    if (prefs.ready) {
      for (const p of prefs.prefs) {
        if (!matchesCustomName(p, query) || rows.some((r) => r.chat_id && prefsFor([p], r.chat_id, r.phone).length)) continue;
        const phone = p.chat_key.endsWith('@c.us') ? p.chat_key.slice(0, -'@c.us'.length) : null;
        rows.push({ key: `pref:${p.chat_key}`, chat_id: p.chat_id, shopify_order_id: null, name: null, phone, order_name: null, stage: null, last_body: null, last_at: null, match: null });
      }
    }
    const pending = server.isFetching || debounced !== query.trim();
    status = pending
      ? t('Recherche dans tout l’historique…')
      : server.isError
        ? t('La recherche complète a échoué — seules les conversations affichées ont été parcourues.')
        : t('{n} résultat(s) · toutes les conversations', { n: String(rows.length) });
  }

  navRef.current = searching
    ? rows.map((r) => r.chat_id).filter((id): id is string => Boolean(id))
    : conversations.map((c) => c.chat_id);
  const navIndex = selected ? navRef.current.indexOf(selected) : -1;

  const selectedConv = listed.find((c) => c.chat_id === selected);
  const pickedHere = picked?.chat_id === selected ? picked : null;
  const head = {
    phone: selectedConv?.phone ?? pickedHere?.phone ?? null,
    name: selectedConv?.name ?? pickedHere?.name ?? null,
    orderName: selectedConv?.order_name ?? pickedHere?.order_name ?? null,
    orders: selectedConv?.orders ?? null,
    stage: selectedConv?.stage ?? pickedHere?.stage ?? null,
  };

  // ─── A PHONE: the list, and the chat as a full screen over it ─────────────
  if (isPhone) {
    return (
      <div className="space-y-2">
        <SearchBox value={query} onChange={setQuery} inputRef={searchRef} />
        <PrefsError prefs={prefs} />
        {searching ? (
          rows.length || server.isFetching || debounced !== query.trim()
            ? <SearchResults rows={rows} selected={selected} onSelect={openFromSearch} status={status} prefs={prefs} />
            : <p className="text-[12.5px] text-gray-600 px-1 py-3">{t('Aucun résultat pour « {q} ».', { q: query.trim() })}</p>
        ) : (<>
          {period.outside > 0 && (
            <p className="text-[11.5px] text-gray-500 px-1 tabular-nums">
              {t('{n} conversation(s) hors de cette période', { n: period.outside })}
              {onShowAll && (<>
                {' · '}
                <button type="button" onClick={onShowAll} className="underline underline-offset-2 cursor-pointer">{t('Tout afficher')}</button>
              </>)}
            </p>
          )}
          <ConversationList conversations={conversations} selected={selected} onSelect={openFromList} prefs={prefs} />
        </>)}
        {selected && (
          <PhoneChat
            chatId={selected} name={head.name} phone={head.phone} orderName={head.orderName} stage={head.stage}
            prefill={prefill} onCompose={(text) => setPrefill({ text, nonce: Date.now() })} onBack={closeChat}
            onNext={openNext} onReturn={openFromList} onStep={(step) => stepRef.current(step)}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={frameRef} data-conversations-frame
      // « Agrandir » = the whole screen (owner, 2026-10-14: « even with expand
      // the top is making it small »): the page title, tabs and menu are
      // covered; « Réduire » or Échap brings them back.
      style={wide && selected ? { height: '100dvh' } : frameH != null ? { height: frameH } : undefined}
      className={`grid grid-cols-1 gap-4 min-h-0 ${wide && selected ? 'fixed inset-0 z-40 p-3 bg-bg-primary' : 'md:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)_340px]'}`}>
      {/* ─── The list, and the search over everything — its own scroll ──────── */}
      <div className={`flex flex-col min-h-0 gap-2 ${wide && selected ? 'hidden' : ''}`}>
        <SearchBox value={query} onChange={setQuery} inputRef={searchRef} />
        <PrefsError prefs={prefs} />
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-2 pr-1" data-no-ptr>
          {searching ? (
            rows.length || server.isFetching || debounced !== query.trim()
              ? <SearchResults rows={rows} selected={selected} onSelect={openFromSearch} status={status} prefs={prefs} />
              : (
                <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-4">
                  <p className="text-[12.5px] font-medium text-gray-800">{t('Aucun résultat pour « {q} ».', { q: query.trim() })}</p>
                  <p className="text-[11.5px] text-gray-500 mt-1 leading-snug">
                    {t('Essayez le numéro de commande sans #, les 9 derniers chiffres du téléphone, ou un mot du message.')}
                  </p>
                </div>
              )
          ) : (
            <>
              {period.outside > 0 && (
                <p className="text-[11.5px] text-gray-500 px-1 tabular-nums">
                  {t('{n} conversation(s) hors de cette période', { n: period.outside })}
                  {onShowAll && (<>
                    {' · '}
                    <button type="button" onClick={onShowAll} className="underline underline-offset-2 hover:text-gray-800 cursor-pointer">
                      {t('Tout afficher')}
                    </button>
                  </>)}
                </p>
              )}
              <ConversationList conversations={conversations} selected={selected} onSelect={openFromList} prefs={prefs} />
            </>
          )}
        </div>
      </div>

      {/* ─── The thread: header + buttons, then the chat — its own scroll ───── */}
      {selected ? (
        <div className="flex flex-col min-h-0 gap-2">
          {/* Below xl the order card sits above the thread, in its own short frame. */}
          <div className={`xl:hidden shrink-0 max-h-[38%] overflow-y-auto overscroll-contain rounded-xl ${wide ? 'hidden' : ''}`} data-no-ptr>
            <OrderPanel chatId={selected} onCompose={(text) => setPrefill({ text, nonce: Date.now() })} />
          </div>
          <div className="shrink-0 flex items-center gap-3 flex-wrap rounded-xl border border-border-subtle bg-bg-card px-3 py-2">
            <PrefsTitle variant="desktop" prefs={prefs} chatId={selected} phone={head.phone}
              name={head.name} number={phoneOf(selected, head.phone)}
              extra={(
                <a href={`https://wa.me/${phoneOf(selected, head.phone)}`} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  {t('Ouvrir dans WhatsApp')}
                </a>
              )} />
            {head.orders && <OrderChips orders={head.orders} reveal="popover" className="shrink-0 hidden lg:block" />}
            {/* ← → : the previous / next chat of the list, untouched (the keys do the same). */}
            <div className="shrink-0 inline-flex rounded-lg border border-border-default overflow-hidden" data-chat-nav>
              <button type="button" onClick={() => stepChat('prev')} disabled={navIndex <= 0}
                aria-label={t('Conversation précédente (←)')} title={t('Conversation précédente (←)')}
                className="h-9 w-9 inline-flex items-center justify-center bg-bg-card text-text-secondary hover:bg-bg-elevated disabled:opacity-40 disabled:cursor-default cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500">
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <button type="button" onClick={() => stepChat('next')} disabled={navIndex >= navRef.current.length - 1}
                aria-label={t('Conversation suivante (→)')} title={t('Conversation suivante (→)')}
                className="h-9 w-9 inline-flex items-center justify-center border-l border-border-default bg-bg-card text-text-secondary hover:bg-bg-elevated disabled:opacity-40 disabled:cursor-default cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500">
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <ChatInboxBar chatId={selected} onNextChat={openNext} onReturnToChat={openFromList} />
            <button type="button" onClick={toggleWide} aria-pressed={wide}
              title={wide ? t('Réafficher la liste et la commande') : t('Agrandir la conversation')}
              className="h-9 px-3 rounded-lg border border-border-default bg-bg-card text-[12.5px] text-text-secondary hover:bg-bg-elevated cursor-pointer">
              {wide ? `⤡ ${t('Réduire')}` : `⤢ ${t('Agrandir')}`}
            </button>
          </div>
          {/* One line, not a card: the thread gets the height (owner, 2026-10-14). */}
          <div className="shrink-0 rounded-xl overflow-hidden border border-border-subtle"><HandoverBar chatId={selected} compact /></div>
          <div className="flex-1 min-h-0 rounded-xl overflow-hidden border border-[var(--wa-frame)]">
            <Thread chatId={selected} phone={head.phone} name={head.name} prefill={prefill} fill />
          </div>
        </div>
      ) : (
        <p className="text-[12.5px] text-gray-400 px-1">{t('Choisissez une conversation.')}</p>
      )}

      {/* ─── The order card beside the thread on wide screens — its own scroll ─ */}
      {selected && !wide && (
        <div className="hidden xl:block min-h-0 overflow-y-auto overscroll-contain pr-1" data-no-ptr>
          <OrderPanel chatId={selected} onCompose={(text) => setPrefill({ text, nonce: Date.now() })} />
        </div>
      )}
    </div>
  );
}

/**
 * The chat on a phone, like WhatsApp (app-feel plan, builder B, 2026-10-01).
 *
 * A full screen over the list: a header with the back arrow, the name and the
 * order chip (the order card opens as a sheet), the handover line, the thread
 * filling the height, the composer on the bottom safe area.
 *
 * ⚠️ A PORTAL TO <body>: a page transition (transform) on an ancestor would
 * otherwise turn `position: fixed` into « fixed to the page ».
 * ⚠️ SIZED TO THE VISUAL VIEWPORT: iOS does not shrink the layout for the
 * keyboard, so the composer would sit under it.
 * ⚠️ `<body data-adm-chat="open">` while it is open: the shell hides its
 * bottom bar (see phone.ts, useChatOpenSignal).
 */
function PhoneChat({ chatId, name, phone, orderName, stage = null, prefill, onCompose, onBack, onNext, onReturn, onStep }: {
  chatId: string; name: string | null; phone: string | null; orderName: string | null; stage?: string | null;
  prefill: ComposerPrefill; onCompose: (text: string) => void; onBack: () => void;
  onNext: (chatId: string | null) => void; onReturn: (chatId: string) => void;
  /** A sideways swipe: the previous / next chat of the list, untouched. */
  onStep?: (step: Step) => boolean;
}) {
  const { t } = useT();
  const [orderOpen, setOrderOpen] = useState(false);
  useEffect(() => { setOrderOpen(false); }, [chatId]);
  // The keyboard is up while a field has focus: the bottom bar steps aside.
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isField = (el: Element | null) => Boolean(el && (['INPUT', 'TEXTAREA'].includes(el.tagName) || (el as HTMLElement).isContentEditable));
    const sync = () => setTyping(isField(document.activeElement));
    const later = () => window.setTimeout(sync, 0);
    document.addEventListener('focusin', sync);
    document.addEventListener('focusout', later);
    return () => { document.removeEventListener('focusin', sync); document.removeEventListener('focusout', later); };
  }, []);
  useChatOpenSignal(true);
  const vv = useVisualViewport(true);
  const number = phoneOf(chatId, phone);
  const prefs = useChatPrefs();
  const status = statusChip(stage);
  // ← → on a phone = a sideways swipe across the chat (owner, 2026-10-30).
  // Not from the left edge (the shell's swipe-back), not in the reply box or a
  // field, not while the order sheet is open, never with two fingers.
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const tgt = e.target as HTMLElement | null;
    touch.current = e.touches.length === 1 && !orderOpen && onStep && !isTypingTarget(tgt) && !tgt?.closest?.('[data-no-swipe]')
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start || !onStep || e.changedTouches.length !== 1) return;
    if (typeof window !== 'undefined' && String(window.getSelection?.() ?? '').length > 0) return;
    const p = e.changedTouches[0];
    const step = swipeStep(start.x, p.clientX - start.x, p.clientY - start.y, window.innerWidth || 390);
    if (step) onStep(step);
  };
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={() => { touch.current = null; }}
      role="dialog" aria-modal="true" aria-label={name || number} data-phone-chat
      className="fixed inset-x-0 z-[60] flex flex-col bg-[var(--wa-wallpaper)] storiz-fade-in"
      style={{ top: vv.top, height: vv.height != null ? vv.height : '100dvh' }}
    >
      <header className="shrink-0 flex items-center gap-1 pr-2 bg-[var(--wa-bar)] border-b border-[var(--wa-header-line)]"
        style={{ paddingTop: 'var(--adm-safe-top, env(safe-area-inset-top, 0px))' }}>
        <button type="button" onClick={onBack} aria-label={t('Retour aux conversations')}
          className="h-12 w-12 shrink-0 inline-flex items-center justify-center text-[var(--wa-icon)] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00a884]">
          <ArrowLeft className="h-6 w-6" aria-hidden />
        </button>
        <PrefsTitle variant="phone" prefs={prefs} chatId={chatId} phone={phone} name={name} number={number} />
        <button type="button" data-order-chip aria-expanded={orderOpen} onClick={() => setOrderOpen((v) => !v)}
          className={`shrink-0 min-h-[36px] max-w-[42%] px-3 rounded-full border text-[12.5px] font-medium tabular-nums truncate cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
            orderOpen ? 'border-brand-400 bg-brand-50 text-brand-800' : 'border-black/10 dark:border-[var(--wa-frame)] bg-[var(--wa-field)] text-[var(--wa-text)]'
          }`}>
          {orderName || t('Commande')}
          {/* The status in the chip itself — the same colour as on the list. */}
          {orderName && status && <ChipPill chip={status} className="ml-1.5 align-middle" />}
        </button>
        <PaymentRequestButton target={{ chat: chatId }} compact short className="rounded-full" />
      </header>
      <HandoverBar chatId={chatId} compact />
      <div className="flex-1 min-h-0 relative">
        <Thread chatId={chatId} phone={phone} name={name} prefill={prefill} fill />
        {orderOpen && (
          // The order card as a sheet over the thread; a tap on the shade closes it.
          <div className="absolute inset-0 z-10 bg-black/30 flex flex-col justify-start" onClick={() => setOrderOpen(false)}>
            <div className="max-h-[80%] overflow-y-auto overscroll-contain bg-white rounded-b-2xl shadow-lg p-3 storiz-fade-in" data-no-ptr
              onClick={(e) => e.stopPropagation()}>
              <OrderPanel chatId={chatId} onCompose={(text) => { onCompose(text); setOrderOpen(false); }} />
            </div>
          </div>
        )}
      </div>
      {/* The three Inbox buttons as a bottom bar — out of the way while she types. */}
      {!typing && <ChatInboxBar chatId={chatId} onNextChat={onNext} onReturnToChat={onReturn} compact />}
    </div>,
    document.body,
  );
}

/**
 * One conversation, whole: header, the three Inbox buttons, the handover line
 * and the live WhatsApp thread with its send box (order page redesign,
 * 2026-10-07 — the chat drawer). The same pieces as this tab, so sending from
 * the order page is the same sending (it pauses the assistant).
 */
export function ChatPanel({ chatId, name = null, phone = null, onClose }: {
  chatId: string; name?: string | null; phone?: string | null; onClose: () => void;
}) {
  const { t } = useT();
  const number = phoneOf(chatId, phone);
  const isPhone = useIsPhone();
  return (
    <div className="flex flex-col h-full min-h-0 bg-bg-card">
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border-subtle">
        <button type="button" onClick={onClose} aria-label={t('Fermer')}
          className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-lg text-text-secondary hover:bg-bg-elevated cursor-pointer">
          {isPhone ? <ArrowLeft className="h-5 w-5" aria-hidden /> : <X className="h-5 w-5" aria-hidden />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-text-primary truncate">{name || number}</p>
          <a href={`https://wa.me/${number}`} target="_blank" rel="noopener noreferrer" className="text-[11.5px] text-text-tertiary underline underline-offset-2 tabular-nums">
            {name ? `${number} · ` : ''}{t('Ouvrir dans WhatsApp')}
          </a>
        </div>
        {!isPhone && <ChatInboxBar chatId={chatId} onNextChat={() => {}} onReturnToChat={() => {}} stay />}
      </div>
      <div className="shrink-0"><HandoverBar chatId={chatId} compact /></div>
      <div className="flex-1 min-h-0">
        <Thread chatId={chatId} phone={phone} name={name} prefill={null} fill />
      </div>
      {isPhone && <ChatInboxBar chatId={chatId} onNextChat={() => {}} onReturnToChat={() => {}} compact stay />}
    </div>
  );
}
